import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import userPreviewIcon from "../../../assets/images/svg/userPreview.svg";
import calendarIcon from "../../../assets/images/svg/calendarMinus.svg";
import depotIcon from "../../../assets/images/svg/depot.svg";
import depotWhiteIcon from "../../../assets/images/svg/depot-white.svg";
import workerIcon from "../../../assets/images/svg/worker.svg";
import blacklisted from "../../../assets/images/svg/NoEntry.svg";
import customerAvailabilityIcon from "../../../assets/images/svg/customerAvailability.svg";
import scheduleCalendarIcon from "../../../assets/images/svg/scheduleCalendar.svg";
import BackButtonLink from "components/Common/buttons/BackButtonLink";
import { ACTION_TYPE } from "pages/customer-management/customers/constants";
import { Tooltip } from "antd";
import classnames from "classnames";
import { USER_ROLES } from "utils/constants";
import { useLocation } from "react-router";
import { userHasOneOfRoles } from "utils/helpers/functions";
import { STATUSES } from "pages/customer-management/customerDetails/constants";
import moment from "moment";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import SetAvailabilityModal from "pages/customer-management/customerDetails/SetAvaliabilityModal";
import useIsMobile from "customHooks/useIsMobile";
import CustomButton from "components/Common/buttons/CustomButton";

const useStyles = makeStyles({
  root: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  imageContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: 15,

    "@media (max-width: 576px)": {
      width: "100%",
    },
  },
  mainInfo: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 8px 16px 0 #00000014",
    borderRadius: 100,
    height: 60,

    "@media (max-width: 576px)": {
      flexDirection: "column",
      height: "auto",
      borderRadius: 12,
      width: "100%",
      alignItems: "flex-start",
      padding: 10,

      "& > div + div": {
        borderTop: "1px solid #EBEBEB",
      },
    },
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    background: "linear-gradient(180deg, #00C2FF 0%, #0085FF 100%)",
    boxShadow: "0 8px 16px 0 #00000014",
    borderRadius: 100,
    height: 60,
    marginLeft: 20,

    "@media (max-width: 576px)": {
      margin: 0,
      background: "white",
      boxShadow: "none",
      borderRadius: 0,
      height: "auto",
      width: "100%",
    },
  },
  iconButton: {
    display: "flex",
    alignItems: "center",
    padding: "0 20px",

    "& img": {
      width: 20,
    },

    "& :hover": {
      cursor: "pointer",
      opacity: 0.7,
    },
  },
  nameContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    marginLeft: 8,

    "& span": {
      "@media (max-width: 576px)": {
        fontSize: 22,
      },

      fontSize: 16,
      fontWeight: 600,
      marginBottom: 4,
      maxWidth: 250,
    },
  },
  status: {
    color: "#FFFFFF",
    fontSize: 9,
    fontWeight: 300,
    backgroundColor: "#17C347",
    textTransform: "uppercase",
    padding: "3px 8px",
    borderRadius: 6,
    letterSpacing: "0.05em",
  },
  iconContainer: {
    padding: 8,
    marginLeft: 23,
    borderRadius: "50%",
    backgroundColor: "#ebebeb",
    width: 65,
    minWidth: 65,
    height: 65,

    "& img": {
      width: "100%",
      height: "100%",
      backgroundColor: "#D9D9D9",
      borderRadius: "50%",
    },
  },
  section: {
    display: "flex",
    alignItems: "center",
    padding: "0 20px",
    width: 180,

    "&>img": {
      width: 30,
      marginRight: 10,
    },

    "@media (max-width: 576px)": {
      width: "100%",
      padding: 10,

      "&>div": {
        display: "flex",
        width: "100%",
      },
    },
  },
  title: {
    color: "#666666",
    fontSize: 9,
    fontWeight: 300,

    "@media (max-width: 576px)": {
      width: "100%",
      display: "flex",
      alignItems: "center",
    },
  },
  data: {
    fontSize: 11,
    fontWeight: 600,

    "@media (max-width: 576px)": {
      textAlign: "end",
      width: "100%",
    },
  },
  overflowText: {
    overflow: "hidden",
    width: 110,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",

    "@media (max-width: 576px)": {
      width: "100%",
    },
  },
  divider: {
    height: "100%",
    width: 2,
    backgroundColor: "#0000000F",
  },
  rightContainer: {
    display: "flex",
    marginTop: 15,

    "@media (max-width: 576px)": {
      justifyContent: "space-between",
      width: "100%",
      alignItems: "center",
      marginBottom: 20,
    },
  },
  buttonsWrapper: {
    display: "flex",

    "@media (max-width: 576px)": {
      display: "flex",
      position: "fixed",
      bottom: 0,
      zIndex: 9,
      background: "white",
      width: "100%",
      left: 0,
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0px -4px 16px 0px rgba(0, 0, 0, 0.08)",
    },
  },
  availabilitySection: {
    width: 225,
    justifyContent: "space-between",

    "@media (max-width: 576px)": {
      width: "100%",
    },
  },
  sectionActionsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(180deg, #00C2FF 0%, #0085FF 100%)",
    boxShadow: "0 8px 16px 0 #00000014",
    borderRadius: 100,
    height: 32,
    width: 32,
  },
  iconButtonSmall: {
    display: "flex",
    alignItems: "center",

    "& :hover": {
      cursor: "pointer",
      opacity: 0.7,
    },
  },
  infoContainer: {
    display: "flex",
    alignItems: "center",
    position: "relative",

    "&>img": {
      width: 30,
      marginRight: 10,
    },
  },
  badge: {
    zIndex: 9,
    borderRadius: "50%",
    backgroundColor: "#444444",
    position: "absolute",
    minWidth: 9,
    height: 9,
    left: 19,
    top: 1,
  },
  notSet: {
    fontWeight: 700,
    color: "#444444",
  },
  notSetBadge: {
    backgroundColor: "#444444",
  },
  available: {
    fontWeight: 700,
    color: "#17C347",
  },
  availableBadge: {
    backgroundColor: "#17C347",
  },
  notAvailable: {
    fontWeight: 700,
    color: "#ff0000",
  },
  notAvailableBadge: {
    backgroundColor: "#ff0000",
  },
  unreachable: {
    fontWeight: 700,
    color: "#DB6607",
  },
  unreachableBadge: {
    backgroundColor: "#DB6607",
  },
  buttonContainer: {
    "@media (max-width: 576px)": {
      width: "100%",
      padding: 10,
    },
  },
});

const color = "#448aff";

const Header = ({
  customer,
  isBlacklisted,
  photoDetails,
  onAction,
  customerAvailability,
  updateCustomerAvailability,
  isCreatedByGCR,
}) => {
  const classes = useStyles();
  const [isSetAvaliabilityOpen, setIsSetAvaliabilityOpen] = useState(false);
  const { firstName, lastName, status, depot, installationDate, sector, type } = customer;
  const handleBtnClick = useCallback(e => onAction(e.currentTarget.dataset.type), []);
  const isMobile = useIsMobile();

  const searchParams = useLocation().search;
  const backUrl = new URLSearchParams(searchParams).get("backUrl");
  const backButtonUrl = useMemo(() => (backUrl ? backUrl : "/customers/customers/active"), [backUrl]);

  const hideRoles = [USER_ROLES.CUSTOMER_CARE, USER_ROLES.CX_INTEGRATION, USER_ROLES.INTERNAL_AUDITOR];

  const customerAvailabilityData = useMemo(() => {
    if (customerAvailability && customerAvailability.unreachable) {
      return {
        text: "UNREACHABLE",
        desc: "Until Tomorrow",
        textColor: classes.unreachable,
        badgeColor: classes.unreachableBadge,
      };
    }
    if (customerAvailability && customerAvailability.availableAt) {
      return {
        text: "AVAILABLE",
        desc: `${moment(customerAvailability.availableAt).format("DD MMM")} - ${customerAvailability.timeSlot}`,
        textColor: classes.available,
        badgeColor: classes.availableBadge,
      };
    }
    return {
      text: "AVAILABILITY NOT SET",
      desc: "",
      textColor: classes.notSet,
      badgeColor: classes.notSetBadge,
    };
  }, [customerAvailability]);

  const onSetAvailability = useCallback(() => setIsSetAvaliabilityOpen(true), []);
  const onSetAvailabilityClose = useCallback(() => setIsSetAvaliabilityOpen(false), []);

  const onLinkTsrClick = useCallback(type => onAction(type), []);

  return (
    <div className={classes.root}>
      <div className={classes.imageContainer}>
        <BackButtonLink to={backButtonUrl}>Back</BackButtonLink>

        {!isMobile && (
          <div className={classes.iconContainer}>
            <img src={photoDetails?.personalPhotoUrl || userPreviewIcon} alt="customer" />
          </div>
        )}
        <div className={classes.nameContainer}>
          <span>
            {firstName && `${firstName} ${lastName}`}{" "}
            {isBlacklisted && <img src={blacklisted} alt="blacklisted customer" />}
          </span>
          {status && <div className={classes.status}>{status}</div>}
        </div>
      </div>

      <div className={classes.rightContainer}>
        <div className={classes.mainInfo}>
          <div className={classes.section}>
            <img src={calendarIcon} alt="date of birth" />
            <div>
              <div className={classes.title}>INSTALL DATE</div>
              <div className={classes.data}>
                {(installationDate ? customDateFormatWithoutTime(installationDate) : null) || "-"}
              </div>
            </div>
          </div>
          <div className={classes.divider} />

          <div className={classes.section}>
            <img src={depotIcon} alt="depot" />
            <div>
              <div className={classes.title}>DEPOT → SECTOR</div>
              <div
                className={classnames(classes.data, classes.overflowText)}
                title={`${depot || "-"} → ${sector || "-"}`}
              >
                {`${depot || "-"} → ${sector || "-"}`}
              </div>
            </div>
          </div>

          <div className={classes.divider} />
          <div className={classnames(classes.section, classes.availabilitySection)}>
            <div className={classes.infoContainer}>
              <img src={customerAvailabilityIcon} alt="customerAvailability" />
              <span className={classnames(classes.badge, customerAvailabilityData?.badgeColor)}></span>
              <div>
                <div className={classnames(classes.title, customerAvailabilityData?.textColor)}>
                  {customerAvailabilityData?.text}
                </div>
                {customerAvailability && (
                  <div className={classnames(classes.data, classes.overflowText)}>{customerAvailabilityData?.desc}</div>
                )}
              </div>
            </div>
            {status && status !== STATUSES.REPOSSESSED && status !== STATUSES.SUSPENDED && (
              <div onClick={onSetAvailability} className={classes.sectionActionsContainer}>
                <div className={classes.iconButtonSmall}>
                  <Tooltip placement="bottom" title="Set Availability" color={color}>
                    <img src={scheduleCalendarIcon} alt="Set Availability" />
                  </Tooltip>
                </div>
              </div>
            )}
          </div>

          {customer?.acquisitionDate && (
            <>
              <div className={classes.divider} />

              <div className={classes.section}>
                <img src={calendarIcon} alt="Acquisition Date" />
                <div>
                  <div className={classes.title}>Acquisition Date</div>
                  <div className={classes.data}>
                    {moment(customer?.acquisitionDate).format("DD MMM")} - {customer?.acquisitionSlot}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className={classes.buttonsWrapper}>
          {userHasOneOfRoles(hideRoles) ? null : (
            <div className={classes.actionsContainer}>
              {status && status !== STATUSES.REPOSSESSED && status !== STATUSES.SUSPENDED && (
                <>
                  {!userHasOneOfRoles([
                    USER_ROLES.SALES,
                    USER_ROLES.CUSTOMER_CARE_LEADERSHIP,
                    USER_ROLES.CUSTOMER_CARE,
                    USER_ROLES.CX_INTEGRATION,
                    USER_ROLES.IT_OPERATIONS,
                    USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                    USER_ROLES.FINANCE,
                    USER_ROLES.GENERAL,
                    USER_ROLES.METER_ENGINEER,
                    USER_ROLES.METER_ENGINEER_LEADERSHIP,
                    USER_ROLES.PRODUCT_TECHNICIANS,
                    USER_ROLES.MWT,
                    USER_ROLES.MDSC,
                  ]) && (
                    <>
                      {!isMobile ? (
                        <div
                          className={classes.iconButton}
                          data-type={ACTION_TYPE.ASSIGN_DEPOT}
                          onClick={handleBtnClick}
                        >
                          <Tooltip placement="bottom" title="Assign depot" color={color}>
                            <img src={depotWhiteIcon} alt="change depot" />
                          </Tooltip>
                        </div>
                      ) : (
                        <CustomButton
                          className={classes.button}
                          containerClassName={classes.buttonContainer}
                          type="primary"
                          color="primary"
                          text="Assign depot"
                          data-type={ACTION_TYPE.ASSIGN_DEPOT}
                          onClick={handleBtnClick}
                          size={isMobile && "small"}
                        />
                      )}
                    </>
                  )}

                  {!userHasOneOfRoles([
                    USER_ROLES.CUSTOMER_CARE_LEADERSHIP,
                    USER_ROLES.CUSTOMER_CARE,
                    USER_ROLES.CX_INTEGRATION,
                    USER_ROLES.IT_OPERATIONS,
                    USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                    USER_ROLES.FINANCE,
                    USER_ROLES.GENERAL,
                    USER_ROLES.METER_ENGINEER,
                    USER_ROLES.METER_ENGINEER_LEADERSHIP,
                    USER_ROLES.PRODUCT_TECHNICIANS,
                    USER_ROLES.MWT,
                    USER_ROLES.MDSC,
                  ]) && (
                    <>
                      {type === "Residential" &&
                        (isMobile ? (
                          <CustomButton
                            className={classes.button}
                            containerClassName={classes.buttonContainer}
                            type="primary"
                            color="primary"
                            text="Link to TSR"
                            data-type={ACTION_TYPE.LINK_TSR}
                            onClick={
                              isCreatedByGCR ? () => onLinkTsrClick("technician") : handleBtnClick
                            }
                            size={isMobile && "small"}
                          />
                        ) : (
                          <>
                            <div className={classes.divider} />
                            <div
                              className={classes.iconButton}
                              data-type={ACTION_TYPE.LINK_TSR}
                              onClick={isCreatedByGCR ? () => onLinkTsrClick("technician") : handleBtnClick}
                            >
                              <Tooltip placement="bottom" title="Link to TSR" color={color}>
                                <img src={workerIcon} alt="change tsr" />
                              </Tooltip>
                            </div>
                          </>
                        ))}
                      {type === "Commercial" &&
                        (isMobile ? (
                          <CustomButton
                            className={classes.button}
                            containerClassName={classes.buttonContainer}
                            type="primary"
                            color="primary"
                            text="Link to TSR"
                            data-type={ACTION_TYPE.LINK_TSR}
                            onClick={() => {
                              onLinkTsrClick("csaTechnician");
                            }}
                            size={isMobile && "small"}
                          />
                        ) : (
                          <>
                            <div className={classes.divider} />
                            <div
                              className={classes.iconButton}
                              data-type={ACTION_TYPE.LINK_TSR}
                              onClick={() => {
                                onLinkTsrClick("csaTechnician");
                              }}
                            >
                              <Tooltip placement="bottom" title="Link to TSR" color={color}>
                                <img src={workerIcon} alt="change tsr" />
                              </Tooltip>
                            </div>
                          </>
                        ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {isSetAvaliabilityOpen && (
        <SetAvailabilityModal
          update={updateCustomerAvailability}
          customerId={customer.id}
          onClose={onSetAvailabilityClose}
        />
      )}
    </div>
  );
};

export default Header;
